/*
 * Copyright (c) 2025, Wultra s.r.o. (www.wultra.com).
 *
 * All rights reserved. This source code can be used only for purposes specified
 * by the given license contract signed by the rightful deputy of Wultra s.r.o.
 * This source code can be used only by the owner of the license.
 *
 * Any disputes arising in respect of this agreement (license) shall be brought
 * before the Municipal Court of Prague.
 */

import { TrashIcon } from "lucide-react"
import { observer } from "mobx-react-lite"

import type { ErrorLog } from "../../logger/logger-store"
import { LoggerViewModel } from "../../logger/logger-viewmodel"
import { formatApiValue } from "../../utils/format-api-value"
import { formatTimestamp } from "../../utils/format-timestamp"
import { valueOrDash } from "../../utils/value-or-dash"
import { useViewModel } from "../app/app-hooks"
import { Button } from "../primitives/button"
import { Card } from "../primitives/card"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../primitives/table"
import { Textarea } from "../primitives/textarea"

export const LoggerView = observer(() => {
    const vm = useViewModel(LoggerViewModel)
    const hasErrors = vm.errors.length > 0

    return (
        <div className="w-content max-inline-7xl">
            <div className="mb-6 flex items-center justify-between">
                <h1 className="m-0 text-3xl font-bold text-primary">Errors</h1>
                <Button
                    variant="destructive"
                    className="font-semibold uppercase"
                    disabled={!hasErrors}
                    onClick={() => vm.clearErrors()}
                >
                    <TrashIcon data-icon="inline-start" />
                    Clear errors
                </Button>
            </div>

            <Card className="py-0">
                <Table>
                    <colgroup>
                        <col className="w-3/20" />
                        <col />
                        <col className="w-1/10" />
                        <col className="w-1/2" />
                    </colgroup>
                    <TableHeader>
                        <TableRow>
                            <TableHead>Timestamp</TableHead>
                            <TableHead>Message</TableHead>
                            <TableHead>HTTP</TableHead>
                            <TableHead>Error</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>{hasErrors ? vm.errors.map(renderLog) : renderEmptyRow()}</TableBody>
                </Table>
            </Card>
        </div>
    )
})

function renderLog(errLog: ErrorLog) {
    return (
        <TableRow key={errLog.timestamp}>
            <TableCell className="whitespace-normal tabular-nums">{formatTimestamp(errLog.timestamp)}</TableCell>
            <TableCell className="whitespace-normal">{valueOrDash(errLog.message)}</TableCell>
            <TableCell className="tabular-nums">{valueOrDash(errLog.apiError?.status?.toString())}</TableCell>
            <TableCell className="whitespace-normal">
                {errLog.apiError?.responseObject && (
                    <Textarea
                        value={formatApiValue(errLog.apiError.responseObject)}
                        readOnly
                        className="h-25 font-mono text-xs text-muted-foreground md:text-xs"
                    />
                )}
                {errLog.appError?.message}
            </TableCell>
        </TableRow>
    )
}

function renderEmptyRow() {
    return (
        <TableRow>
            <TableCell colSpan={4} className="h-24 text-center text-muted-foreground">
                No errors
            </TableCell>
        </TableRow>
    )
}
